import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, inject, OnDestroy } from '@angular/core';
import { ActionMenuComponent } from './action-menu/action-menu.component';
import { TableAction } from './table.model';

/**
 * Floating action menu (desktop) anchored to a button: the row "⋮" menu and the add-button menu.
 * Provided per table, so the open menu is disposed when the table goes away.
 */
@Injectable()
export class TableActionMenuService implements OnDestroy {
	private readonly overlay = inject(Overlay);
	private overlayRef: OverlayRef | null = null;

	/** Opens the menu under `anchor` (above it when there is no room); a previous one closes first */
	open(anchor: HTMLElement, actions: TableAction[], rowData?: unknown): void {
		this.close();

		const positionStrategy = this.overlay
			.position()
			.flexibleConnectedTo(anchor)
			.withPositions([
				{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
				{ originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
			]);

		const overlayRef = this.overlay.create({
			positionStrategy,
			hasBackdrop: true,
			backdropClass: 'cdk-overlay-transparent-backdrop',
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
		});
		this.overlayRef = overlayRef;

		const menu = overlayRef.attach(new ComponentPortal(ActionMenuComponent)).instance;
		menu.actions = actions;
		if (rowData !== undefined) menu.rowData = rowData;

		// Closes after an action, on a click outside or with Escape
		menu.actionExecuted.subscribe(() => this.close());
		overlayRef.backdropClick().subscribe(() => this.close());
		overlayRef.keydownEvents().subscribe((event) => {
			if (event.key === 'Escape') this.close();
		});
	}

	close(): void {
		this.overlayRef?.dispose();
		this.overlayRef = null;
	}

	ngOnDestroy(): void {
		this.close();
	}
}
